<script setup lang="ts">
    import { reactive, inject } from 'vue';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import Loading from '@shared/components/Loading.vue';
    import { bsAlert } from '@cwd/shared/components/BsAlerts';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { SubscriptionAccess } from '@views/BackOffice/Subscription/Subscription.access';
    import { FormEl } from '@cwd/shared/components/Form/Form';
    import { NewAccountRequest } from '../../User/AccountReference';
    import * as accountService from '../../Account/Account.access';

    const props = defineProps<{
        onComplete?: () => void;
    }>();

    const emit = defineEmits(['cancelled']);

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionAccess: SubscriptionAccess = injectorAccess.injector.get(SubscriptionAccess);
    const isInputValid = (inputValue: string): boolean => {
        return !!inputValue && inputValue.trim().length > 0;
    };

    const state = reactive<{
        creatingAccount: boolean,
        newAccountForm: {
            data: FormEl
        }
    }>({
        creatingAccount: false,
        newAccountForm: {
            data: {
                AccountName: {
                    modelValue: '',
                    input: {
                        label: 'Account Name',
                        type: 'text' as const,
                        maxlength: 255,
                        placeholder: 'Enter Account Name',
                    },
                    required: true,
                    isValid: false,
                    invalidMessage: 'Account name is required',
                },
                DefaultReplyToAddress: {
                    modelValue: '',
                    input: {
                        label: 'Default ReplyTo-Address',
                        type: 'text' as const,
                        maxlength: 100,
                        placeholder: 'Enter ReplyTo-Address',
                    },
                    required: true,
                    isValid: false,
                    invalidMessage: 'Default ReplyTo-Address is required',
                },
                DefaultName: {
                    modelValue: '',
                    input: {
                        label: 'Default from name',
                        type: 'text' as const,
                        maxlength: 100,
                        placeholder: 'Enter Default from name',
                    },
                    required: true,
                    isValid: false,
                    invalidMessage: 'Default from name is required',
                }
            }
        },
    });

    function handleInput(key: string, newVal: any) {
        var input = state.newAccountForm.data[key as keyof typeof state.newAccountForm.data];
        input.modelValue = newVal;
        input.isValid = isInputValid(newVal);
        }
        
    function isFormValid(): { hasErrors: boolean, errorKeys: string[] } {
        var hasErrors = false;
        var errorKeys: string[] = [];
        for (const [key, value] of Object.entries(state.newAccountForm.data)) {
            if (value.required && !value.isValid) {
                hasErrors = true;
                errorKeys.push(key);
        }
    }
        return { hasErrors, errorKeys };
    }

    function cancel(): void {
        state.newAccountForm.data.AccountName.modelValue = '';
        state.newAccountForm.data.AccountName.isValid = false;
        state.newAccountForm.data.DefaultReplyToAddress.modelValue = '';
        state.newAccountForm.data.DefaultName.modelValue = '';

        emit('cancelled');
    }

    async function createAccount(): Promise<void> {
        const formValidationResult = isFormValid();
        if (formValidationResult.hasErrors) {
            formValidationResult.errorKeys.forEach((key) => {
                bsAlert({
                    type: 'danger',
                    title: 'Validation Error',
                    content: state.newAccountForm.data[key as keyof typeof state.newAccountForm.data].invalidMessage!
                });
            });
            return;
        }

        state.creatingAccount = true;

        try {
            if (!subscriptionAccess.state.boServiceSubscription) {
                throw new Error('Insufficient user rights to perform this action');
            }

            const payload: NewAccountRequest = {
                Name: state.newAccountForm.data.AccountName.modelValue,
                ClientCompanyId: subscriptionAccess.state.boServiceSubscription.ClientCompanyId,
                DefaultReplyToAddress: state.newAccountForm.data.DefaultReplyToAddress.modelValue || '',
                DefaultFromName: state.newAccountForm.data.DefaultName.modelValue || ''
            };

            await accountService.createAccount(payload);

            // Refresh accounts list
            await subscriptionAccess.getClientCompanyAccounts();

            bsAlert({
                type: 'success',
                title: 'Success',
                content: 'Account created successfully'
            });

            props.onComplete?.();
            
            // Resets and hides the form
            cancel();
        } catch (error) {
            bsAlert({
                type: 'danger',
                title: 'Error',
                content: 'Failed to create account: ' + (error instanceof Error ? error.message : 'Unknown error')
            });
        } finally {
            state.creatingAccount = false;
        }
    }
</script>

<template>
    <div>
        <Loading :show="state.creatingAccount" loadingStyle="progress-bar"></Loading>

        <div v-if="!state.creatingAccount">
            <Form :data="state.newAccountForm.data"
                  :submitting="state.creatingAccount"
                  :hide-submit="true"
                  @update:modelValue="handleInput">
            </Form>
        </div>

        <div class="form-group">
            <button type="button"
                    class="btn btn-primary"
                    @click.prevent="createAccount"
                    v-bind:disabled="state.creatingAccount">
                Create
            </button>
            <button type="button"
                    class="btn btn-default m-xs-l"
                    @click.prevent="cancel">
                Cancel
            </button>
        </div>
    </div>
</template>

<style>
</style>